import i18next from 'i18next';
import { jwtDecode } from 'jwt-decode';
import {
  ICountry,
  ICurrency,
  ISettlementMethod,
  IStatementTemplateType,
  ISupportedLanguage,
} from 'services/enums/types';
import { ISettingsResponse } from 'services/settings/application/types';
import { IOwnerLogoResponse } from 'services/settings/individual/types';
import { ITheme } from 'services/theme/types';
import { ISelectOption } from 'types/components/inputs/input.interface';
import { createStore, useStore } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export type TokenDataPermissions = {
  'AdvancePermissionsLIST**'?: string | string[];
  'ArtistPermissionsLIST**'?: string | string[];
  'AuthPermissionsLIST**'?: string | string[];
  'ContractPermissionsLIST**'?: string | string[];
  'DigitalDeliveryPermissionsLIST**'?: string | string[];
  'EventPermissionsLIST**'?: string | string[];
  'LabelPermissionsLIST**'?: string | string[];
  'LabelReportPermissionsLIST**'?: string | string[];
  'MerchPermissionsLIST**'?: string | string[];
  'NotificationPermissionsLIST**'?: string | string[];
  'ProvidersPermissionsLIST**'?: string | string[];
  'SalesChannelPermissionsLIST**'?: string | string[];
  'SettlementCostPermissionsLIST**'?: string | string[];
  'SettlementIncomePermissionsLIST**'?: string | string[];
  'SettlementOrderPermissionsLIST**'?: string | string[];
  'SettlementPeriodPermissionsLIST**'?: string | string[];
  'SettlementPermissionsLIST**'?: string | string[];
  'StatementPermissionsLIST**'?: string | string[];
  'TrackPermissionsLIST**'?: string | string[];
  'ChannelPermissionsLIST**'?: string | string[];
  'ContractTemplatePermissionsLIST**'?: string | string[];
  'DigitalMusicProductPermissionsLIST**'?: string | string[];
  'LabelReportOrderPermissionsLIST**'?: string | string[];
  'PhysicalMusicProductPermissionsLIST**'?: string | string[];
  'StatisticsPermissionsLIST**'?: string | string[];
  'FinanceStatisticPermissionsLIST**'?: string | string[];
  'CodesDistributorPermissionsLIST**'?: string | string[];
  'VideoPermissionsLIST**'?: string | string[];
  'ServicePermissionsLIST**'?: string | string[];
};

type TokenData = {
  iat: number;
  jti: string;
  AdvancePermissions?: string;
  ArtistPermissions?: string;
  AuthPermissions?: string;
  ContractPermissions?: string;
  DigitalDeliveryPermissions?: string;
  LabelPermissions?: string;
  LabelReportPermissions?: string;
  MerchPermissions?: string;
  NotificationPermissions?: string;
  ProvidersPermissions?: string;
  SalesChannelPermissions?: string;
  SettlementCostPermissions?: string;
  SettlementIncomePermissions?: string;
  SettlementOrderPermissions?: string;
  SettlementPeriodPermissions?: string;
  SettlementPermissions?: string;
  StatementPermissions?: string;
  TrackPermissions?: string;
  ChannelPermissions?: string;
  ContractTemplatePermissions?: string;
  DigitalMusicProductPermissions?: string;
  LabelReportOrderPermissions?: string;
  PhysicalMusicProductPermissions?: string;
  StatisticsPermissions?: string;
  FinanceStatisticPermissions?: string;
  UserId: string;
  FirstName: string;
  LastName: string;
  UserName: string;
  Email: string;
  nbf: number;
  exp: number;
  iss: string;
  aud: string;
} & TokenDataPermissions;

type RefreshTokenData = {
  salt: string;
  userId: string;
  aud: string;
  exp: number;
  iat: number;
  iss: string;
  jti: string;
  nbf: number;
};

export type ICookiesStore = {
  cookiesAccepted: boolean;
  setCookiesAccepted: () => void;
};
export type AuthStore = {
  token: string | null;
  tokenData: TokenData | null;
  refreshToken: string | null;
  refreshTokenData: RefreshTokenData | null;
  twoFactorTokenExpiresAt: number | null;
  serviceTokenExpiresAt: number | null;
  isTokenStale: boolean;
  setToken: (token: string | null) => void;
  setRefreshToken: (refreshToken: string | null) => void;
  setTwoFactorTokenExpiresAt: (expires: number | null) => void;
  setServiceTokenExpiresAt: (expires: number | null) => void;
  clearTokens: () => void;
  setIsTokenStale: (stale: boolean) => void;
};

export interface IColumnsVisibilityStore {
  hiddenColumns: number[];
  toggleColumn: (column: number) => void;
  isColumnHidden: (column: number) => boolean;
  isColumnDisabled: (column: number) => boolean;
}

export interface IEnumStore {
  countries: ICountry[];
  getCountriesOptions: (property?: keyof ICountry) => ISelectOption<string>[];
  setCountries: (countries?: ICountry[]) => void;
  languages: ISupportedLanguage[];
  getLanguagesOptions: () => ISelectOption<string>[];
  setLanguages: (languages?: ISupportedLanguage[]) => void;
  settlementMethods: ISettlementMethod[];
  getSettlementMethodsOptions: () => ISelectOption<number>[];
  setSettlementMethods: (settlementMethods?: ISettlementMethod[]) => void;
  currencies: ICurrency[];
  getCurrenciesOptions: (property?: keyof ICurrency) => ISelectOption[];
  setCurrencies: (currencies?: ICurrency[]) => void;
  statementTemplateTypes: IStatementTemplateType[];
  getStatementTemplateTypesOptions: () => ISelectOption<number>[];
  setStatementTemplateTypes: (
    statementTemplateTypes?: IStatementTemplateType[],
  ) => void;
}

export interface ISettingsStore {
  applicationSettings: ISettingsResponse | null;
  setApplicationSettings: (settings: ISettingsResponse) => void;
  defaultCurrency: ICurrency | null;
  setDefaultCurrency: (currency: ICurrency) => void;
}

export interface IOwnerLogoStore {
  ownerLogo: IOwnerLogoResponse | null;
  setOwnerLogo: (ownerLogo: IOwnerLogoResponse | null) => void;
}

interface ITableConfig {
  grid: string;
  hiddenColumns: number[];
  columnsNumber?: number;
  minimumColumns?: number;
}

export interface ITablesConfig {
  tablesConfig: Record<string, ITableConfig>;
  changeTableConfig: (id: string, config: Partial<ITableConfig>) => void;
  toggleTableColumn: (tableId: string, column: number) => void;
  isTableColumnHidden: (tableId: string, column: number) => boolean;
  isTableColumnDisabled: (tableId: string, column: number) => boolean;
}

export interface ISearchStore {
  searchById: Record<string, any>;
  setSearchById: (id: string, search: any) => void;
}

export enum THEME_TYPE {
  light = 1,
  dark = 2,
}
export interface IThemeStore {
  themeType: THEME_TYPE | null;
  setThemeType: (type: THEME_TYPE) => void;
  theme: ITheme | null;
  setTheme: (theme: ITheme) => void;
}

export interface IVersionStore {
  version: string;
  latestVersion: string;
  setVersion: (version: string) => void;
  setLatestVersion: (version: string) => void;
}

export interface IHomeViewStore {
  selectedView: 'calendar' | 'spotify-statistics' | 'finance-statistics';
  setSelectedView: (
    view: 'calendar' | 'spotify-statistics' | 'finance-statistics',
  ) => void;
}

export interface IUserSettingsStore {
  soundVolume: number;
  setSoundVolume: (volume?: number) => void;
  isSoundMuted: boolean;
  setIsSoundMuted: (isMuted?: boolean) => void;
}

export interface IPlayerStore {
  playerTrackId: string | null;
  setPlayerTrackId: (playerTrackId: string | null) => void;
  trackVolume: number;
  setTrackVolume: (volume?: number) => void;
  videoVolume: number;
  setVideoVolume: (volume?: number) => void;
}

export interface IQueryFilterDropdownStore {
  dropdownStates: Record<string, boolean>;
  setDropdownState: (domain: string, isOpen: boolean) => void;
  getDropdownState: (domain: string) => boolean;
}

export type IStore = AuthStore &
  ICookiesStore &
  IEnumStore &
  ISettingsStore &
  IOwnerLogoStore &
  ITablesConfig &
  ISearchStore &
  IThemeStore &
  IVersionStore &
  IHomeViewStore &
  IUserSettingsStore &
  IPlayerStore &
  IQueryFilterDropdownStore;

const decodeToken = (token: string): TokenData | null => {
  if (!token) return null;
  return jwtDecode(token);
};

const decodeRefreshToken = (refreshToken: string): RefreshTokenData | null => {
  if (!refreshToken) return null;
  return jwtDecode(refreshToken);
};

export const stateStore = createStore<IStore>()(
  persist(
    (set, get) => ({
      version: '',
      latestVersion: '',
      setVersion: version => set(state => ({ version: version })),
      setLatestVersion: version => set(state => ({ latestVersion: version })),
      isTokenStale: true,
      setIsTokenStale: (isStale = true) =>
        set(state => ({ isTokenStale: isStale })),
      cookiesAccepted: false,
      setCookiesAccepted: () => set(state => ({ cookiesAccepted: true })),
      token: null,
      tokenData: null,
      refreshToken: null,
      refreshTokenData: null,
      twoFactorTokenExpiresAt: null,
      serviceTokenExpiresAt: null,
      tablesConfig: {} as Record<string, ITableConfig>,
      changeTableConfig: (id: string, config: Partial<ITableConfig>) => {
        set(state => ({
          tablesConfig: {
            ...state.tablesConfig,
            [id]: {
              grid: config.grid ?? state.tablesConfig[id]?.grid ?? '',
              hiddenColumns:
                config.hiddenColumns ??
                state.tablesConfig[id]?.hiddenColumns ??
                [],
              columnsNumber:
                config.columnsNumber ?? state.tablesConfig[id]?.columnsNumber,
              minimumColumns:
                config.minimumColumns ??
                state.tablesConfig[id]?.minimumColumns ??
                2,
            },
          },
        }));
      },
      toggleTableColumn: (tableId: string, column: number) => {
        set(state => {
          const tableConfig = state.tablesConfig[tableId];
          if (!tableConfig) return state;

          const isHidden = tableConfig.hiddenColumns.includes(column);
          const columnsNumber = tableConfig.columnsNumber ?? 0;
          const minimumColumns = tableConfig.minimumColumns ?? 2;

          if (
            !isHidden &&
            columnsNumber - tableConfig.hiddenColumns.length <= minimumColumns
          ) {
            return state;
          }

          const updatedHiddenColumns = isHidden
            ? tableConfig.hiddenColumns.filter(c => c !== column)
            : [...tableConfig.hiddenColumns, column];

          return {
            tablesConfig: {
              ...state.tablesConfig,
              [tableId]: {
                ...tableConfig,
                hiddenColumns: updatedHiddenColumns,
              },
            },
          };
        });
      },
      isTableColumnHidden: (tableId: string, column: number) => {
        const tableConfig = get().tablesConfig[tableId];
        return tableConfig?.hiddenColumns.includes(column) ?? false;
      },
      isTableColumnDisabled: (tableId: string, column: number) => {
        const tableConfig = get().tablesConfig[tableId];
        if (!tableConfig) return false;

        const columnsNumber = tableConfig.columnsNumber ?? 0;
        const minimumColumns = tableConfig.minimumColumns ?? 2;
        return (
          columnsNumber - tableConfig.hiddenColumns.length <= minimumColumns &&
          !tableConfig.hiddenColumns.includes(column)
        );
      },
      setToken: (token: string | null) => {
        const decodedToken = decodeToken(token as string);
        const tokenData = (() => {
          try {
            return decodedToken;
          } catch (error) {
            console.error(error);
            return null;
          }
        })();
        set({
          token,
          tokenData,
        });
      },
      setRefreshToken: (refreshToken: string | null) => {
        const decodedRefreshToken = decodeRefreshToken(refreshToken as string);
        const refreshTokenData = (() => {
          try {
            return decodedRefreshToken;
          } catch (error) {
            console.error(error);
            return null;
          }
        })();
        set({
          refreshToken,
          refreshTokenData,
        });
      },
      setTwoFactorTokenExpiresAt: (expires: number | null) => {
        const twoFactorTokenExpiresAt = expires ? Date.now() + expires : 0;
        set({
          twoFactorTokenExpiresAt,
        });
      },
      setServiceTokenExpiresAt: (expires: number | null) => {
        const serviceTokenExpiresAt = expires ? Date.now() + expires : 0;
        set({
          serviceTokenExpiresAt,
        });
      },
      clearTokens: () => {
        set({
          token: null,
          tokenData: null,
          refreshTokenData: null,
          refreshToken: null,
          twoFactorTokenExpiresAt: null,
        });
      },
      countries: [],
      getCountriesOptions: property =>
        get().countries.map(c => ({
          label: i18next.t(`countries.${c.codeAlfa2}`),
          value: c[property ?? 'codeAlfa2'],
        })),
      setCountries: countries => set({ countries: countries ?? [] }),
      languages: [],
      getLanguagesOptions: () =>
        get().languages.map(l => ({
          label: i18next.t(`enum.supportedLanguages.${l.language}`),
          value: l.language,
        })),
      setLanguages: languages => set({ languages: languages ?? [] }),
      settlementMethods: [],
      getSettlementMethodsOptions: () =>
        get().settlementMethods.map(s => ({
          label: i18next.t(`enum.settlementMethods.${s.name}`),
          value: s.id,
        })),
      setSettlementMethods: settlementMethods =>
        set({ settlementMethods: settlementMethods ?? [] }),
      currencies: [],
      getCurrenciesOptions: property =>
        get().currencies.map(c => ({
          label: c.currencyCode,
          value: c[property ?? 'id'],
        })),
      setCurrencies: currencies => set({ currencies: currencies ?? [] }),
      statementTemplateTypes: [],
      getStatementTemplateTypesOptions: () =>
        get().statementTemplateTypes.map(s => ({
          label: s.name,
          value: s.id,
        })),
      setStatementTemplateTypes: statementTemplateTypes =>
        set({ statementTemplateTypes: statementTemplateTypes ?? [] }),
      applicationSettings: null,
      setApplicationSettings: settings =>
        set({ applicationSettings: settings }),
      defaultCurrency: null,
      setDefaultCurrency: currency => set({ defaultCurrency: currency }),
      ownerLogo: null,
      setOwnerLogo: ownerLogo => set({ ownerLogo }),
      searchById: {},
      setSearchById: (id: string, search: any) => {
        set(state => ({
          searchById: {
            ...state.searchById,
            [id]: search,
          },
        }));
      },
      theme: null,
      setTheme: (theme: ITheme) =>
        set(state => ({
          theme: theme,
        })),
      themeType: null,
      setThemeType: (themeType: THEME_TYPE) => {
        localStorage.setItem('theme-mode', `${themeType}`);
        set(state => ({
          themeType: themeType,
        }));
      },
      selectedView: 'calendar',
      setSelectedView: (
        view: 'calendar' | 'spotify-statistics' | 'finance-statistics',
      ) => {
        set(state => ({
          selectedView: view,
        }));
      },
      soundVolume: 0.5,
      setSoundVolume: volume => set({ soundVolume: volume ?? 0.5 }),
      isSoundMuted: false,
      setIsSoundMuted: isMuted => set({ isSoundMuted: !!isMuted }),
      playerTrackId: null,
      setPlayerTrackId: playerTrackId => set({ playerTrackId }),
      trackVolume: 1,
      setTrackVolume: volume =>
        set({
          trackVolume: volume === undefined ? 1 : volume,
        }),
      videoVolume: 1,
      setVideoVolume: volume =>
        set({
          videoVolume: volume === undefined ? 1 : volume,
        }),
      dropdownStates: {},
      setDropdownState: (domain: string, isOpen: boolean) => {
        set(state => ({
          dropdownStates: {
            ...state.dropdownStates,
            [domain]: isOpen,
          },
        }));
      },
      getDropdownState: (domain: string) => {
        return get().dropdownStates[domain] ?? false;
      },
    }),
    {
      name: 'store',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

export type ExtractState<S> = S extends {
  getState: () => infer T;
}
  ? T
  : never;

type Params<U> = Parameters<typeof useStore<typeof stateStore, U>>;

const tokenSelector = (state: ExtractState<typeof stateStore>) => state.token;
const tokenDataSelector = (state: ExtractState<typeof stateStore>) =>
  state.tokenData;
const refreshTokenDataSelector = (state: ExtractState<typeof stateStore>) =>
  state.refreshTokenData;
const refreshTokenSelector = (state: ExtractState<typeof stateStore>) =>
  state.refreshToken;
const twoFactorTokenExpiresAtSelector = (
  state: ExtractState<typeof stateStore>,
) => state.twoFactorTokenExpiresAt;
const countriesSelector = (state: ExtractState<typeof stateStore>) =>
  state.countries;
const getCountriesOptionsSelector = (state: ExtractState<typeof stateStore>) =>
  state.getCountriesOptions;
const applicationSettingsSelector = (state: ExtractState<typeof stateStore>) =>
  state.applicationSettings;
const defaultCurrencySelector = (state: ExtractState<typeof stateStore>) =>
  state.defaultCurrency;

const actionsSelector = (state: ExtractState<typeof stateStore>) => state;

export const getAccessToken = () => tokenSelector(stateStore.getState());
export const getAccessTokenData = () =>
  tokenDataSelector(stateStore.getState());
export const getRefreshTokenData = () =>
  refreshTokenDataSelector(stateStore.getState());
export const getRefreshToken = () =>
  refreshTokenSelector(stateStore.getState());
export const getTwoFactorTokenExpiresAt = () =>
  twoFactorTokenExpiresAtSelector(stateStore.getState());
export const getCountries = () => countriesSelector(stateStore.getState());
export const getCountriesOptions = () =>
  getCountriesOptionsSelector(stateStore.getState());
export const getApplicationSettings = () =>
  applicationSettingsSelector(stateStore.getState());
export const getDefaultCurrency = () =>
  defaultCurrencySelector(stateStore.getState());

export const getActions = () => actionsSelector(stateStore.getState());

const useStateStore = <U>(selector: Params<U>[1]) => {
  return useStore(stateStore, selector);
};

export const useStateStorage = () => useStateStore(stateStore.getState);

export const useToken = () => useStateStore(tokenSelector);
export const useTokenData = () => useStateStore(tokenDataSelector);
export const useRefreshTokenData = () =>
  useStateStore(refreshTokenDataSelector);
export const useRefreshToken = () => useStateStore(refreshTokenSelector);
export const useTwoFactorTokenExpiresAt = () =>
  useStateStore(twoFactorTokenExpiresAtSelector);
export const useCountries = () => useStateStore(countriesSelector);
export const useCountriesOptions = () =>
  useStateStore(getCountriesOptionsSelector);

export const useActions = () => useStateStore(actionsSelector);

export const useTablesConfig = () => useStateStore(state => state.tablesConfig);
export const useChangeTableConfig = () =>
  useStateStore(state => state.changeTableConfig);
export const useToggleTableColumn = () =>
  useStateStore(state => state.toggleTableColumn);
export const useIsTableColumnHidden = () =>
  useStateStore(state => state.isTableColumnHidden);
export const useIsTableColumnDisabled = () =>
  useStateStore(state => state.isTableColumnDisabled);

export const useSearchById = () => useStateStore(state => state.searchById);
export const useSetSearchById = () =>
  useStateStore(state => state.setSearchById);

export const useSelectedView = () => useStateStore(state => state.selectedView);
export const useSetSelectedView = () =>
  useStateStore(state => state.setSelectedView);

export const useQueryFilterDropdownState = (domain: string) => {
  const isOpen = useStateStore(state => state.getDropdownState(domain));
  const setDropdownState = useStateStore(state => state.setDropdownState);
  return {
    isOpen,
    setDropdownState: (isOpen: boolean) => setDropdownState(domain, isOpen),
  };
};
